/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, forkJoin} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {AdUnitApiService, AdUnitModel} from './ad-unit-api.service';
import {resetDimensionsBasedOnAdUnitOption} from './ad-unit-commons';
import {AdUnitCopyModalComponent} from './ad-unit-copy-modal.component';
import {AdUnitItemPageComponent} from './ad-unit-item-page.component';
import {AdUnitListPageComponent} from './ad-unit-list-page.component';

export function adUnitStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(AdUnitApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.managePublishing.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisher.apiKey);
  const rh = adnRouterHelper.createRouterHelper('ad-units.ad-unit', AdUnitApiService, 'app.inv', 'app');
  const copyConfig = {
    modalComponent: AdUnitCopyModalComponent, stateUrl: 'app.inv.ad-units.ad-unit', lcase: 'ad unit', ucase: 'Ad Unit',
    preCopy: resetDimensionsBasedOnAdUnitOption
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: AdUnitListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      adUnitPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: AdUnitModel = {
            id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: [], tags: {},
            urlAsCategory: true, pageSize: 1, targetingControl: 'NONE'
          };
          return new SignalAdminResourcePageState(
            model, true, readOnly(), api, 'Ad Unit', 'name', copyConfig, undefined,
            {objectTypeKey: 'AdUnit', crumbParams: ['EarningsAccount', 'Site']}
          );
        }
        return firstValueFrom(forkJoin({
          model: api.get($stateParams.id),
          tags: api.getTags($stateParams.id)
        })).then(({model, tags}) => {
          model.tags = tags || {};
          userProfileSaver.saveObj(model, 'adnLastViewed');
          return new SignalAdminResourcePageState(
            model, false, readOnly(), api, 'Ad Unit', 'name', copyConfig, undefined,
            {objectTypeKey: 'AdUnit', crumbParams: ['EarningsAccount', 'Site']}
          );
        });
      },
      model: (adUnitPage: SignalAdminResourcePageState<AdUnitModel>) => adUnitPage.model()
    },
    views: {
      'content@app': {component: AdUnitItemPageComponent, bindings: {state: 'adUnitPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'adUnitPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'adUnitPage'}}
    }
  }));
  return states.build();
}
